import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Download, Search, RefreshCw, Users, FileText, Filter, Gift, Crown, LogOut, ShieldCheck, Mail
} from "lucide-react";
import api from "@/utils/api";
import { AdminPromoCodesTab } from "@/components/admin/AdminPromoCodesTab";
import { AdminSubscribersTab } from "@/components/admin/AdminSubscribersTab";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { adminHeaders, setAdminToken } from "@/components/admin/adminSession";

const PAGE_SIZE = 25;

function PlanBadge({ status }) {
  let cls;
  if (status === "active") cls = "bg-green-500/20 text-green-400";
  else if (status === "trial") cls = "bg-amber-500/20 text-amber-400";
  else cls = "bg-zinc-700 text-zinc-300";
  return <Badge className={cls}>{status || "free"}</Badge>;
}

function LeadRow({ lead }) {
  const phoneText = `${lead.country_code || ""} ${lead.phone || "—"}`.trim();
  const signup = lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—";
  const peptides = (lead.peptides_used || []).join(", ") || "—";
  return (
    <tr className="border-t border-zinc-800/50 hover:bg-zinc-900/30">
      <td className="p-3 text-white">{lead.name || "—"}</td>
      <td className="p-3 text-zinc-300">{lead.email}</td>
      <td className="p-3 text-zinc-400">{phoneText}</td>
      <td className="p-3 text-zinc-500 text-xs">{signup}</td>
      <td className="p-3"><PlanBadge status={lead.subscription_status} /></td>
      <td className="p-3 text-zinc-300">{lead.total_cycles ?? 0}</td>
      <td className="p-3 text-zinc-400 max-w-xs truncate">{peptides}</td>
    </tr>
  );
}

export default function AdminPage() {
  const [adminEmail, setAdminEmail] = useState("");
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [peptideFilter, setPeptideFilter] = useState("");
  const [protocolFilter, setProtocolFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const verify = useCallback(async () => {
    try {
      const res = await api.get("/admin/me", { headers: adminHeaders() });
      setAdminEmail(res.data.email);
      setHasAccess(true);
    } catch {
      setHasAccess(false);
    } finally {
      setAccessChecked(true);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  const [sendingSummary, setSendingSummary] = useState(false);
  const sendWeeklySummary = async () => {
    setSendingSummary(true);
    try {
      const res = await api.post("/admin/weekly-summary/send", {}, { headers: adminHeaders() });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send summary");
    } finally {
      setSendingSummary(false);
    }
  };

  const handleLogout = async () => {
    try { await api.post("/admin/auth/logout", {}, { headers: adminHeaders() }); } catch { /* already signed out */ }
    setAdminToken(null);
    setHasAccess(false);
    toast.success("Signed out of admin");
  };

  const fetchLeads = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", PAGE_SIZE);
      params.set("skip", skip);
      if (search) params.set("search", search);
      if (peptideFilter) params.set("peptide_filter", peptideFilter);
      if (protocolFilter) params.set("protocol_filter", protocolFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const res = await api.get(`/admin/leads?${params.toString()}`, { headers: adminHeaders() });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [hasAccess, skip, search, peptideFilter, protocolFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (hasAccess) fetchLeads();
  }, [hasAccess, fetchLeads]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (peptideFilter) params.set("peptide_filter", peptideFilter);
      if (protocolFilter) params.set("protocol_filter", protocolFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const res = await api.get(`/admin/export-leads?${params.toString()}`, {
        headers: adminHeaders(),
        responseType: format === "csv" ? "blob" : "json",
      });

      if (format === "csv") {
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const resetFilters = () => {
    setSearch(""); setPeptideFilter(""); setProtocolFilter("");
    setDateFrom(""); setDateTo(""); setSkip(0);
  };

  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!hasAccess) return <AdminLogin onSuccess={verify} />;

  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const leadRows = leads.map((lead) => (
    <LeadRow key={lead.id || lead.email} lead={lead} />
  ));

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin dashboard</h1>
              <p className="text-xs text-zinc-500" data-testid="admin-signed-in-as">{adminEmail} · {total.toLocaleString()} total leads</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => handleExport("csv")} data-testid="export-csv">
              <Download className="w-4 h-4 mr-2" />CSV
            </Button>
            <Button variant="outline" onClick={() => handleExport("json")} data-testid="export-json">
              <FileText className="w-4 h-4 mr-2" />JSON
            </Button>
            <Button variant="outline" onClick={sendWeeklySummary} disabled={sendingSummary} data-testid="admin-send-weekly-summary">
              <Mail className="w-4 h-4 mr-2" />{sendingSummary ? "Sending…" : "Email weekly summary"}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-2" />Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <Tabs defaultValue="subscribers" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="admin-tab-subscribers">
              <Crown className="w-4 h-4 mr-2" />Subscribers
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="admin-tab-leads">
              <Users className="w-4 h-4 mr-2" />Leads
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="admin-tab-promos">
              <Gift className="w-4 h-4 mr-2" />Promo Codes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="space-y-6">
            <Card className="bg-zinc-950/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Filter className="w-5 h-5 text-cyan-400" />Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">Search (email/name/phone)</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-600" />
                  <Input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
                    placeholder="e.g., john@..."
                    className="pl-9 bg-zinc-900 border-zinc-800"
                    data-testid="admin-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Peptide used</Label>
                <Input
                  value={peptideFilter}
                  onChange={(e) => { setPeptideFilter(e.target.value); setSkip(0); }}
                  placeholder="e.g., BPC-157"
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="admin-peptide-filter"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Protocol / goal</Label>
                <Input
                  value={protocolFilter}
                  onChange={(e) => { setProtocolFilter(e.target.value); setSkip(0); }}
                  placeholder="e.g., fat loss"
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="admin-protocol-filter"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="admin-date-from"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="admin-date-to"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={fetchLeads} className="btn-primary flex-1" data-testid="admin-apply">
                  <RefreshCw className="w-4 h-4 mr-2" />Refresh
                </Button>
                <Button onClick={resetFilters} variant="outline" data-testid="admin-reset">
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-950/50 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 flex justify-center"><div className="spinner" /></div>
            ) : leads.length === 0 ? (
              <div className="p-12 text-center text-zinc-500" data-testid="admin-empty">
                No leads match your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-leads-table">
                  <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Phone</th>
                      <th className="text-left p-3">Signup</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-left p-3">Cycles</th>
                      <th className="text-left p-3">Peptides</th>
                    </tr>
                  </thead>
                  <tbody>{leadRows}</tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <span className="text-zinc-500 text-sm" data-testid="admin-page-info">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={skip === 0 || loading}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              data-testid="admin-prev-page"
            >Previous</Button>
            <Button
              variant="outline"
              disabled={skip + PAGE_SIZE >= total || loading}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              data-testid="admin-next-page"
            >Next</Button>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="subscribers">
            <AdminSubscribersTab />
          </TabsContent>

          <TabsContent value="promos">
            <AdminPromoCodesTab getAuthHeader={adminHeaders} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
